import { FC, useEffect } from 'react';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { AuthHeading, BackButton, Button, Input, Spinner } from 'view/components';
import {
  AuthHeadingImageTypeEnums,
  commonEmailValidationRules,
  parseError,
} from 'view/pages/auth/helpers';
import { AuthFormsFooter } from 'view/pages/auth/components';
import { useForgotPassword } from 'view/pages/auth/hooks/useForgotPassword';
import * as CS from 'view/pages/auth/commonStyled';
import { useNavigate } from 'react-router';

interface IFormInput {
  email: string;
}

const ForgotPasswordFormFields: FC<{
  error: { message: string };
  isLoading: boolean;
  status: number;
}> = ({ error, isLoading, status }) => {
  const navigate = useNavigate();

  const {
    clearErrors,
    setError,
    formState: { errors },
  } = useFormContext<IFormInput>();

  useEffect(() => {
    if (!error && status === 204) {
      clearErrors('email');
      navigate('/auth/forgot/check-email', { replace: true });
    } else if (error)
      setError(
        'email',
        { type: 'custom', message: error.message || 'Unknown error' },
        { shouldFocus: true }
      );
  }, [error, status, setError]);

  return (
    <>
      <Input
        name="email"
        label="Email"
        type="email"
        rules={commonEmailValidationRules}
        parseError={parseError}
      />
      <CS.FormWrapperMain component="div">
        <Button
          variant="contained"
          type="submit"
          text="Request Password Reset"
          disabled={!!errors?.email || isLoading}
          endIcon={
            isLoading ? (
              <Spinner disabled={!!errors?.email || isLoading} variant="contained" />
            ) : null
          }
        />
      </CS.FormWrapperMain>
    </>
  );
};

const ForgotPasswordPage = () => {
  const { isLoading, send, error, status } = useForgotPassword();

  const onSubmit = (formData: IFormInput) => {
    send({ email: formData.email });
  };

  return (
    <CS.Wrapper>
      <CS.BackButtonBox>
        <BackButton text="Back to Sign In" fallbackUrl="/auth/login" />
      </CS.BackButtonBox>
      <CS.AuthMainBox component="section">
        <AuthHeading
          title="Forgot Password"
          subTitle={
            'Please, enter your email address. We’ll send a verification \n link to reset your password'
          }
          imageType={AuthHeadingImageTypeEnums.LINE}
        />
        <CS.FormWrapper component="section">
          <FormContainer defaultValues={{ email: '' }} onSuccess={onSubmit}>
            <ForgotPasswordFormFields error={error} isLoading={isLoading} status={status} />
          </FormContainer>
          <AuthFormsFooter
            title="New to Injobnito?"
            hrefTo="/auth/registration"
            hrefText="Join Now"
          />
        </CS.FormWrapper>
      </CS.AuthMainBox>
    </CS.Wrapper>
  );
};

export default ForgotPasswordPage;
